import React, { useState, useEffect } from "react";
import "./Devices.css";
import Sidebar from "../Sidebar/Sidebar";
import api from "../../api";
import {
  FaDesktop,
  FaTrash,
  FaSync,
  FaCheckCircle,
  FaExclamationTriangle,
} from "react-icons/fa";

const Devices = () => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deactivating, setDeactivating] = useState(null); // device id being deactivated
  const [tier, setTier] = useState("FREE");
  const [maxDevices, setMaxDevices] = useState(1);
  const [total, setTotal] = useState(0);

  const fetchDevices = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/devices/list");
      setDevices(res.data.devices || []);
      setTotal(res.data.total || 0);
      setTier(res.data.tier || "FREE");
      setMaxDevices(res.data.max_devices);
    } catch (err) {
      console.error("Failed to fetch devices:", err);
      if (err.response?.status === 401) {
        setError("Your session has expired. Please log in again.");
      } else {
        setError("Failed to load devices. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDevices();
  }, []);

  const handleDeactivate = async (deviceId, deviceName) => {
    if (
      !window.confirm(
        `Deactivate "${deviceName}"?\n\nThis will free up a device slot. The device will need to log in again to re-register.`
      )
    )
      return;

    setDeactivating(deviceId);
    try {
      await api.delete(`/devices/${deviceId}`);
      setDevices((prev) => prev.filter((d) => d.id !== deviceId));
      setTotal((prev) => prev - 1);
    } catch (err) {
      console.error("Deactivate error:", err);
      alert(
        `Failed to deactivate device: ${err.response?.data?.detail || err.message}`
      );
    } finally {
      setDeactivating(null);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatLastSeen = (iso) => {
    if (!iso) return "—";
    const now = new Date();
    const seen = new Date(iso);
    const diffMs = now - seen;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hr ago`;
    if (diffDays === 1) return "Yesterday";
    return `${diffDays} days ago`;
  };

  const isUnlimited = maxDevices === "Unlimited" || maxDevices === null;
  const usagePercent = isUnlimited ? 0 : Math.min((total / maxDevices) * 100, 100);
  const atLimit = !isUnlimited && total >= maxDevices;

  return (
    <div className="devices-layout">
      <Sidebar />

      <div className="devices-container">
        {/* ── Header ── */}
        <div className="devices-header">
          <div className="devices-header-left">
            <FaDesktop className="devices-header-icon" />
            <div>
              <h2 className="devices-title">My Devices</h2>
              <p className="devices-subtitle">
                Registered desktop devices for your pharmacy
              </p>
            </div>
          </div>

          <button
            className="devices-refresh-btn"
            onClick={fetchDevices}
            disabled={loading}
            title="Refresh device list"
          >
            <FaSync
              style={{
                animation: loading ? "spin 1s linear infinite" : "none",
              }}
            />
          </button>
        </div>

        {/* ── Plan usage card ── */}
        <div className="devices-usage-card">
          <div className="devices-usage-top">
            <div>
              <span className="devices-usage-label">Plan</span>
              <span className={`devices-tier-badge devices-tier-${tier.toLowerCase()}`}>
                {tier}
              </span>
            </div>
            <div className="devices-usage-count">
              <span className="devices-usage-number">{total}</span>
              <span className="devices-usage-sep">/</span>
              <span className="devices-usage-max">
                {isUnlimited ? "∞" : maxDevices}
              </span>
              <span className="devices-usage-unit">devices</span>
            </div>
          </div>

          {!isUnlimited && (
            <div className="devices-progress-track">
              <div
                className={`devices-progress-fill ${atLimit ? "at-limit" : ""}`}
                style={{ width: `${usagePercent}%` }}
              />
            </div>
          )}

          {atLimit && (
            <div className="devices-limit-warning">
              <FaExclamationTriangle style={{ marginRight: "6px" }} />
              Device limit reached. Deactivate an existing device to add a new
              one, or upgrade your plan.
            </div>
          )}

          {isUnlimited && (
            <div className="devices-unlimited-note">
              <FaCheckCircle style={{ marginRight: "6px", color: "#22c55e" }} />
              Unlimited devices on your {tier} plan
            </div>
          )}
        </div>

        {/* ── Error ── */}
        {error && <div className="devices-error">{error}</div>}

        {/* ── Loading ── */}
        {loading && (
          <div className="devices-loading">
            <FaSync style={{ animation: "spin 1s linear infinite" }} />
            <span>Loading devices...</span>
          </div>
        )}

        {/* ── Empty state ── */}
        {!loading && !error && devices.length === 0 && (
          <div className="devices-empty">
            <FaDesktop className="devices-empty-icon" />
            <p>No devices registered yet.</p>
            <span>
              Devices are registered automatically when you log in from the
              desktop app while online.
            </span>
          </div>
        )}

        {/* ── Device list ── */}
        {!loading && devices.length > 0 && (
          <div className="devices-list">
            {devices.map((device, index) => (
              <div
                key={device.id}
                className="devices-card"
                style={{ animationDelay: `${index * 60}ms` }}
              >
                <div className="devices-card-left">
                  <div className="devices-card-icon">
                    <FaDesktop />
                  </div>
                  <div className="devices-card-info">
                    <div className="devices-card-name">
                      {device.device_name || "Unnamed Device"}
                    </div>
                    <div className="devices-card-os">
                      {device.os_info || "Unknown OS"}
                    </div>
                    <div className="devices-card-dates">
                      <span>First seen: {formatDate(device.first_seen)}</span>
                      <span className="devices-card-dot">·</span>
                      <span>Last seen: {formatLastSeen(device.last_seen)}</span>
                    </div>
                  </div>
                </div>

                <div className="devices-card-right">
                  <span
                    className={`devices-status-badge ${
                      device.is_active ? "active" : "inactive"
                    }`}
                  >
                    {device.is_active ? "● Active" : "○ Inactive"}
                  </span>

                  {device.is_active && (
                    <button
                      className="devices-deactivate-btn"
                      onClick={() =>
                        handleDeactivate(
                          device.id,
                          device.device_name || "this device"
                        )
                      }
                      disabled={deactivating === device.id}
                      title="Deactivate this device"
                    >
                      {deactivating === device.id ? (
                        <FaSync
                          style={{ animation: "spin 1s linear infinite" }}
                        />
                      ) : (
                        <FaTrash />
                      )}
                      {deactivating === device.id ? "Removing..." : "Deactivate"}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Info note ── */}
        {!loading && (
          <div className="devices-info-note">
            <strong>How it works:</strong> Devices are registered automatically
            each time you log in from the desktop app while online. Deactivating
            a device frees up a slot — the device can re-register on the next
            online login.
          </div>
        )}
      </div>
    </div>
  );
};

export default Devices;